import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Flex,
  Text,
  SimpleGrid,
  Button,
  Input,
  Select,
  IconButton,
  Badge,
  useBreakpointValue,
  useToast,
  Stack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
} from "@chakra-ui/react";
import {
  useDisclosure,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from "@chakra-ui/react";

import { EditIcon, DeleteIcon } from "lucide-react";
import { RiDeleteBin7Line, RiEditLine } from "react-icons/ri";
import { baseUrl } from "../../utils/utils";

const CreditApprovalSection = ({
  // Props for state and functions
  creditLimit,
  setCreditLimit,
  creditLimitControl,
  setCreditLimitControl,
  creditDays,
  setCreditDays,
  creditDaysControl,
  setCreditDaysControl,
  discounts,
  setDiscounts,
  editingDiscountIndex,
  setEditingDiscountIndex,
  isEditingCredit,
  setIsEditingCredit,
  isSubmitting,
  isUnderReview,
  isApproved,
  isRejected,
  shouldShowEditButton,
  latestCreditEntry,
  full_product_list,
  all_vendor_list,
  selectedVendor,
  setSelectedVendor,
  handleAddDiscount,
  handleChange,
  handleDelete,
  handleEdit,
  handleSaveDiscount,
  handleApprove,
  handleReject,
  handleSaveChanges,
  handleEditCredit,
  handleCancelEdit,
  onCommentOpen,
  getProductsByType,
  isViewOnly,
  toast,
  setVendorError,
}) => {
  const gridColumns = useBreakpointValue({ base: 1, sm: 2, md: 3, lg: 5 });

  // Validation errors state
  const [validationErrors, setValidationErrors] = useState({
    creditLimit: "",
    creditLimitControl: "",
    creditDays: "",
    creditDaysControl: "",
    discounts: {}, // For discount-specific errors
  });

  const [isFirstApproval, setIsFirstApproval] = React.useState(true);
  const [activeDiscountInput, setActiveDiscountInput] = useState({}); // { index: 'percentage' | 'amount' }
  // Create refs to track initial state
  const initialCreditData = useRef({
    creditLimit,
    creditLimitControl,
    creditDays,
    creditDaysControl,
    discounts: JSON.parse(JSON.stringify(discounts)),
  });

  // Classic color scheme
  const classicColors = {
    primary: "#2c3e50",
    secondary: "#34495e",
    accent: "#3498db",
    success: "#27ae60",
    warning: "#f39c12",
    danger: "#e74c3c",
    light: "#ecf0f1",
    dark: "#2c3e50",
    tableHeader: "#228B22",
    tableStripe: "#f8f9fa",
  };

  // Function to check if there are any changes
  const hasChanges = () => {
    const currentData = {
      creditLimit,
      creditLimitControl,
      creditDays,
      creditDaysControl,
      discounts,
      selectedVendor: [...selectedVendor].sort(), // ✅ Sort for proper comparison
    };

    const initialData = {
      ...initialCreditData.current,
      selectedVendor: [
        ...(initialCreditData.current.selectedVendor || []),
      ].sort(), // ✅ Sort for proper comparison
    };

    // For arrays like selectedVendor, we need proper comparison
    const currentVendorStr = JSON.stringify(currentData.selectedVendor);
    const initialVendorStr = JSON.stringify(initialData.selectedVendor);

    // Check if vendors changed
    if (currentVendorStr !== initialVendorStr) {
      console.log("Vendor change detected:", {
        current: currentData.selectedVendor,
        initial: initialData.selectedVendor,
      });
      return true;
    }

    // Check other fields
    const otherFieldsChanged =
      currentData.creditLimit !== initialData.creditLimit ||
      currentData.creditLimitControl !== initialData.creditLimitControl ||
      currentData.creditDays !== initialData.creditDays ||
      currentData.creditDaysControl !== initialData.creditDaysControl ||
      JSON.stringify(currentData.discounts) !==
        JSON.stringify(initialData.discounts);

    if (otherFieldsChanged) {
      console.log("Other fields changed");
    }

    return otherFieldsChanged;
  };

  // Update initial data when editing starts
  useEffect(() => {
    // Set initial data only once when component mounts
    if (!initialCreditData.current.creditLimit) {
      initialCreditData.current = {
        creditLimit,
        creditLimitControl,
        creditDays,
        creditDaysControl,
        discounts: JSON.parse(JSON.stringify(discounts)),
      };
    }
  }, []);

  useEffect(() => {
    if (isEditingCredit) {
      initialCreditData.current = {
        creditLimit,
        creditLimitControl,
        creditDays,
        creditDaysControl,
        discounts: JSON.parse(JSON.stringify(discounts)),
      };
    }
  }, [isEditingCredit]);

  // Clear validation errors when discounts array changes
  useEffect(() => {
    // Clear discount validation errors when discounts array changes
    setValidationErrors((prev) => ({
      ...prev,
      discounts: {},
    }));
  }, [discounts.length]); // Reset when discounts length changes

  // Validation function
  const validateForm = () => {
    const errors = {
      creditLimit: "",
      creditLimitControl: "",
      creditDays: "",
      creditDaysControl: "",
      discounts: {},
    };

    let hasErrors = false;

    // Validate credit limit
    if (!creditLimit || creditLimit === "" || Number(creditLimit) <= 0) {
      errors.creditLimit =
        "Credit limit is required and must be greater than 0";
      hasErrors = true;
    }

    // Validate credit limit control
    if (!creditLimitControl || creditLimitControl === "") {
      errors.creditLimitControl = "Credit limit control is required";
      hasErrors = true;
    }

    // Validate credit days
    if (!creditDays || creditDays === "" || Number(creditDays) <= 0) {
      errors.creditDays = "Credit days is required and must be greater than 0";
      hasErrors = true;
    }

    // Validate credit days control
    if (!creditDaysControl || creditDaysControl === "") {
      errors.creditDaysControl = "Credit days control is required";
      hasErrors = true;
    }

    // Validate discounts
    discounts.forEach((discount, index) => {
      const discountErrors = {};

      if (!discount.type || discount.type === "") {
        discountErrors.type = "Type is required";
        hasErrors = true;
      }

      if (!discount.product || discount.product === "") {
        discountErrors.product = "Product is required";
        hasErrors = true;
      }

      if (
        !discount.discount ||
        discount.discount === "" ||
        Number(discount.discount) <= 0
      ) {
        discountErrors.discount =
          "Discount is required and must be greater than 0";
        hasErrors = true;
      }

      if (!discount.effectiveDate || discount.effectiveDate === "") {
        discountErrors.effectiveDate = "Effective date is required";
        hasErrors = true;
      }

      if (Object.keys(discountErrors).length > 0) {
        errors.discounts[index] = discountErrors;
      }
    });

    setValidationErrors(errors);
    return !hasErrors;
  };

  // Enhanced save changes handler with validation and change detection
  // Enhanced save changes handler with validation and change detection
  const handleSaveChangesWithValidation = () => {
    if (!hasChanges()) {
      toast({
        title: "No changes to save",
        description: "You haven't made any changes to save.",
        status: "info",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (validateForm()) {
      setValidationErrors({
        creditLimit: "",
        creditLimitControl: "",
        creditDays: "",
        creditDaysControl: "",
        discounts: {},
      });
      onSaveOpen(); // Open save confirmation dialog
    }
  };

  // Add new function to confirm save
  const confirmSaveChanges = () => {
    handleSaveChanges();
    onSaveClose();
  };

  // Enhanced approve handler with validation
  const handleApproveWithValidation = (status) => {
    if (!validateForm()) return;

    if (isFirstApproval && selectedVendor.length === 0) {
      setVendorError("Vendor selection is mandatory for first approval.");
      return;
    }

    setVendorError(""); // clear error if valid
    handleApprove(status);
    setIsFirstApproval(false);
  };

  const handleDeleteWithValidation = async (index) => {
    const discountToDelete = discounts[index];

    try {
      const formData = new FormData();
      formData.append("action", "delete_product");
      formData.append("id", discountToDelete.id);

      const response = await fetch(baseUrl + "credit.php", {
        method: "POST",
        body: formData,
      });

      const data = await response.json();

      if (data.success) {
        // Step 3: If API call is successful, proceed with removing the discount from state
        handleDelete(index);

        // Clear validation errors for the deleted discount
        setValidationErrors((prev) => {
          const updatedDiscountErrors = { ...prev.discounts };
          delete updatedDiscountErrors[index];

          // Re-index the remaining errors
          const reindexedErrors = {};
          Object.keys(updatedDiscountErrors).forEach((key) => {
            const oldIndex = parseInt(key);
            if (oldIndex > index) {
              reindexedErrors[oldIndex - 1] = updatedDiscountErrors[oldIndex];
            } else if (oldIndex < index) {
              reindexedErrors[oldIndex] = updatedDiscountErrors[oldIndex];
            }
          });

          return {
            ...prev,
            discounts: reindexedErrors,
          };
        });
      } else {
        // Handle the error if the API response is not successful
        toast({
          title: "Delete Failed",
          description: "Could not delete the discount. Please try again.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error deleting product:", error);
      toast({
        title: "Delete Failed",
        description:
          "An error occurred while deleting the product. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef();
  const [deleteIndex, setDeleteIndex] = useState(null);
  const {
    isOpen: isSaveOpen,
    onOpen: onSaveOpen,
    onClose: onSaveClose,
  } = useDisclosure();
  const saveConfirmRef = useRef();

  const confirmDelete = () => {
    if (deleteIndex !== null) {
      handleDeleteWithValidation(deleteIndex);
      setDeleteIndex(null);
    }
    onClose();
  };

  const renderDiscountRow = (row, index, isMobile = false) => {
    const isEditing = editingDiscountIndex === index;
    const isNewProduct = row.isNew;
    const shouldDisableEditing = isRejected;
    const shouldShowActions = !isRejected || isViewOnly;
    const discountErrors = validationErrors.discounts[index] || {};

    if (isMobile) {
      return (
        <Box
          key={index}
          p={4}
          border="1px solid"
          borderColor={
            discountErrors.type ||
            discountErrors.product ||
            discountErrors.discount ||
            discountErrors.effectiveDate
              ? "red.200"
              : "gray.300"
          }
          bg={
            discountErrors.type ||
            discountErrors.product ||
            discountErrors.discount ||
            discountErrors.effectiveDate
              ? "red.50"
              : "white"
          }
          mb={3}
          borderRadius="md"
          boxShadow="sm"
          position="relative"
        >
          {(discountErrors.type ||
            discountErrors.product ||
            discountErrors.discount ||
            discountErrors.effectiveDate) && (
            <Box
              position="absolute"
              top={0}
              left={0}
              right={0}
              height="4px"
              bg="red.500"
              borderTopRadius="md"
            />
          )}

          <Flex justify="space-between" align="center" mb={3}>
            <Text fontSize="sm" fontWeight="bold" color={classicColors.primary}>
              Discount #{index + 1}
            </Text>

            {isNewProduct && (
              <Badge
                bg={classicColors.success}
                color="white"
                ml={2}
                fontSize="xs"
                borderRadius="md"
              >
                New
              </Badge>
            )}

            {shouldShowActions && (
              <Flex>
                {isEditing ? (
                  <IconButton
                    size="xs"
                    bg={classicColors.success}
                    color="white"
                    icon={<Text fontSize="xs">✓</Text>}
                    onClick={() => handleSaveDiscount(index)}
                    mr={2}
                    borderRadius="md"
                    _hover={{ bg: "#229954" }}
                  />
                ) : (
                  <IconButton
                    size="xs"
                    bg={classicColors.accent}
                    color="white"
                    icon={<EditIcon size={14} />}
                    onClick={() => handleEdit(index)}
                    mr={2}
                    isDisabled={shouldDisableEditing}
                    borderRadius="md"
                    _hover={{ bg: "#2980b9" }}
                  />
                )}
                <IconButton
                  size="xs"
                  bg={classicColors.danger}
                  color="white"
                  icon={<DeleteIcon size={14} />}
                  onClick={() => {
                    setDeleteIndex(index);
                    onOpen();
                  }}
                  isDisabled={shouldDisableEditing}
                  borderRadius="md"
                  _hover={{ bg: "#c0392b" }}
                />
              </Flex>
            )}
          </Flex>

          <Stack spacing={3}>
            {/* Type */}
            <Box>
              <Text
                fontSize="xs"
                fontWeight="bold"
                mb={1}
                color={classicColors.dark}
              >
                Type
              </Text>
              <Select
                value={row.type}
                onChange={(e) => handleChange(index, "type", e.target.value)}
                size="sm"
                placeholder="Select type"
                isDisabled={!isEditing || shouldDisableEditing}
                borderColor={discountErrors.type ? "red.500" : "gray.400"}
                bg={discountErrors.type ? "red.50" : "white"}
                _focus={{
                  borderColor: discountErrors.type
                    ? "red.500"
                    : classicColors.accent,
                  boxShadow: `0 0 0 1px ${discountErrors.type ? "red.500" : classicColors.accent}`,
                  bg: "white",
                }}
              >
                {[...new Set(full_product_list.map((p) => p.type))].map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </Select>
              {discountErrors.type && (
                <Text color="red.500" fontSize="xs" mt={1}>
                  {discountErrors.type}
                </Text>
              )}
            </Box>

            {/* Product */}
            <Box>
              <Text
                fontSize="xs"
                fontWeight="bold"
                mb={1}
                color={classicColors.dark}
              >
                Product
              </Text>
              <Select
                value={row.product}
                onChange={(e) => handleChange(index, "product", e.target.value)}
                size="sm"
                placeholder="Select product"
                isDisabled={!row.type || !isEditing || shouldDisableEditing}
                borderColor={discountErrors.product ? "red.500" : "gray.400"}
                bg={discountErrors.product ? "red.50" : "white"}
                _focus={{
                  borderColor: discountErrors.product
                    ? "red.500"
                    : classicColors.accent,
                  boxShadow: `0 0 0 1px ${discountErrors.product ? "red.500" : classicColors.accent}`,
                  bg: "white",
                }}
              >
                {getProductsByType(row.type, row.product).map((p) => (
                  <option key={p.uuid} value={p.uuid}>
                    {p.name}
                  </option>
                ))}
              </Select>
              {discountErrors.product && (
                <Text color="red.500" fontSize="xs" mt={1}>
                  {discountErrors.product}
                </Text>
              )}
            </Box>

            <SimpleGrid columns={2} spacing={3}>
              <Box>
                <Text
                  fontSize="xs"
                  fontWeight="bold"
                  mb={1}
                  color={classicColors.dark}
                >
                  RSP (Per Cyl.)
                </Text>
                <Input
                  value={row.rspCyl}
                  size="sm"
                  type="number"
                  readOnly
                  bg="gray.50"
                  borderColor="gray.400"
                />
              </Box>
              <Box>
                <Text
                  fontSize="xs"
                  fontWeight="bold"
                  mb={1}
                  color={classicColors.dark}
                >
                  RSP (Per Kg)
                </Text>
                <Input
                  value={row.rspKg}
                  size="sm"
                  type="number"
                  readOnly
                  bg="gray.50"
                  borderColor="gray.400"
                />
              </Box>
            </SimpleGrid>

            {/* Discount */}
            <Box>
              <Text
                fontSize="xs"
                fontWeight="bold"
                mb={1}
                color={classicColors.dark}
              >
                Discount on RSP (Per Cyl.)
              </Text>
              <Box position="relative">
                <Input
                  value={row.discount}
                  onChange={(e) =>
                    handleChange(index, "discount", e.target.value)
                  }
                  size="sm"
                  type="number"
                  isDisabled={!isEditing || shouldDisableEditing}
                  borderColor={discountErrors.discount ? "red.500" : "gray.400"}
                  bg={discountErrors.discount ? "red.50" : "white"}
                  pr="30px"
                  _focus={{
                    borderColor: discountErrors.discount
                      ? "red.500"
                      : classicColors.accent,
                    boxShadow: `0 0 0 1px ${discountErrors.discount ? "red.500" : classicColors.accent}`,
                    bg: "white",
                  }}
                />
                <Text
                  position="absolute"
                  right="8px"
                  top="8px"
                  color="gray.500"
                  fontSize="sm"
                  pointerEvents="none"
                >
                  %
                </Text>
              </Box>
              {discountErrors.discount && (
                <Text color="red.500" fontSize="xs" mt={1}>
                  {discountErrors.discount}
                </Text>
              )}
            </Box>

            {/* Effective Date */}
            <Box>
              <Text
                fontSize="xs"
                fontWeight="bold"
                mb={1}
                color={classicColors.dark}
              >
                Effective Date
              </Text>
              <Input
                type="date"
                value={row.effectiveDate}
                onChange={(e) =>
                  handleChange(index, "effectiveDate", e.target.value)
                }
                size="sm"
                isDisabled={!isEditing || shouldDisableEditing}
                borderColor={
                  discountErrors.effectiveDate ? "red.500" : "gray.400"
                }
                bg={discountErrors.effectiveDate ? "red.50" : "white"}
                _focus={{
                  borderColor: discountErrors.effectiveDate
                    ? "red.500"
                    : classicColors.accent,
                  boxShadow: `0 0 0 1px ${discountErrors.effectiveDate ? "red.500" : classicColors.accent}`,
                  bg: "white",
                }}
              />
              {discountErrors.effectiveDate && (
                <Text color="red.500" fontSize="xs" mt={1}>
                  {discountErrors.effectiveDate}
                </Text>
              )}
            </Box>
          </Stack>
        </Box>
      );
    }

    // Desktop Table Row
    return (
      <>
        <Tr
          key={index}
          _hover={{ bg: classicColors.tableStripe }}
          bg={
            discountErrors.type ||
            discountErrors.product ||
            discountErrors.discount ||
            discountErrors.effectiveDate
              ? "red.50"
              : "transparent"
          }
          borderLeft={
            discountErrors.type ||
            discountErrors.product ||
            discountErrors.discount ||
            discountErrors.effectiveDate
              ? "4px solid red"
              : "none"
          }
        >
          {/* Type */}
          <Td p={3} borderColor="gray.300" verticalAlign="top">
            <Box>
              <Select
                value={row.type}
                onChange={(e) => handleChange(index, "type", e.target.value)}
                size="sm"
                width="100%"
                minW="120px"
                placeholder="Select type"
                isDisabled={!isEditing || shouldDisableEditing}
                borderColor={discountErrors.type ? "red.500" : "gray.400"}
                bg={discountErrors.type ? "red.50" : "white"}
                _focus={{
                  borderColor: discountErrors.type
                    ? "red.500"
                    : classicColors.accent,
                  boxShadow: `0 0 0 1px ${discountErrors.type ? "red.500" : classicColors.accent}`,
                  bg: "white",
                }}
              >
                {[...new Set(full_product_list.map((p) => p.type))].map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </Select>
              {discountErrors.type && (
                <Text color="red.500" fontSize="xs" mt={1} whiteSpace="nowrap">
                  {discountErrors.type}
                </Text>
              )}
            </Box>
          </Td>

          {/* Product */}
          <Td p={3} borderColor="gray.300" verticalAlign="top">
            <Box>
              <Select
                value={row.product}
                onChange={(e) => handleChange(index, "product", e.target.value)}
                size="sm"
                width="100%"
                minW="140px"
                placeholder="Select product"
                isDisabled={!row.type || !isEditing || shouldDisableEditing}
                borderColor={discountErrors.product ? "red.500" : "gray.400"}
                bg={discountErrors.product ? "red.50" : "white"}
                _focus={{
                  borderColor: discountErrors.product
                    ? "red.500"
                    : classicColors.accent,
                  boxShadow: `0 0 0 1px ${discountErrors.product ? "red.500" : classicColors.accent}`,
                  bg: "white",
                }}
              >
                {getProductsByType(row.type, index).map((p) => (
                  <option key={p.uuid} value={p.uuid}>
                    {p.name || p.productname || `Product ${p.uuid}`}
                  </option>
                ))}
                {/* Show existing selection if missing */}
                {row.product &&
                  !getProductsByType(row.type, index).find(
                    (p) => p.uuid === row.product,
                  ) && (
                    <option key={row.product} value={row.product}>
                      {full_product_list.find((p) => p.uuid === row.product)
                        ?.name ||
                        full_product_list.find((p) => p.uuid === row.product)
                          ?.productname ||
                        `Product ${row.product}`}
                    </option>
                  )}
              </Select>
              {discountErrors.product && (
                <Text color="red.500" fontSize="xs" mt={1} whiteSpace="nowrap">
                  {discountErrors.product}
                </Text>
              )}
            </Box>
          </Td>

          {/* RSP Per Cyl */}
          <Td
            p={3}
            borderColor="gray.300"
            textAlign="right"
            verticalAlign="top"
          >
            <Input
              value={row.rspCyl}
              size="sm"
              type="number"
              readOnly
              bg="gray.50"
              borderColor="gray.400"
              textAlign="right"
              width="100%"
            />
          </Td>

          {/* RSP Per Kg */}
          <Td
            p={3}
            borderColor="gray.300"
            textAlign="right"
            verticalAlign="top"
          >
            <Input
              value={row.rspKg}
              size="sm"
              type="number"
              readOnly
              bg="gray.50"
              borderColor="gray.400"
              textAlign="right"
              width="100%"
            />
          </Td>

          {/* Discount */}
          <Td p={3} borderColor="gray.300" verticalAlign="top">
            <Box>
              <Box position="relative">
                <Input
                  value={row.discount}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value > 100) {
                      toast({
                        title: "Invalid Discount",
                        description: "Discount cannot exceed 100%",
                        status: "error",
                        duration: 3000,
                        isClosable: true,
                      });
                      return;
                    }
                    handleChange(index, "discount", value);
                  }}
                  onFocus={() => {
                    setActiveDiscountInput({
                      ...activeDiscountInput,
                      [index]: "percentage",
                    });
                  }}
                  onBlur={(e) => {
                    setActiveDiscountInput({
                      ...activeDiscountInput,
                      [index]: null,
                    });
                    if (e.target.value > 100) {
                      handleChange(index, "discount", 100);
                      toast({
                        title: "Discount Adjusted",
                        description: "Discount has been set to maximum 100%",
                        status: "warning",
                        duration: 3000,
                        isClosable: true,
                      });
                    }
                  }}
                  size="sm"
                  type="number"
                  isDisabled={!isEditing || shouldDisableEditing}
                  pr="30px"
                  max={100}
                  min={0}
                  textAlign="right"
                  width="100%"
                  borderColor={discountErrors.discount ? "red.500" : "gray.400"}
                  bg={discountErrors.discount ? "red.50" : "white"}
                  _focus={{
                    borderColor: discountErrors.discount
                      ? "red.500"
                      : classicColors.accent,
                    boxShadow: `0 0 0 1px ${discountErrors.discount ? "red.500" : classicColors.accent}`,
                    bg: "white",
                  }}
                />
                <Text
                  position="absolute"
                  right="8px"
                  top="8px"
                  color="gray.500"
                  fontSize="sm"
                  pointerEvents="none"
                >
                  %
                </Text>
              </Box>
              {discountErrors.discount && (
                <Text color="red.500" fontSize="xs" mt={1} whiteSpace="nowrap">
                  {discountErrors.discount}
                </Text>
              )}
            </Box>
          </Td>

          {/* Discounted Amount (Deducted) */}
          {/* Discounted Amount (Deducted) */}
          <Td
            p={3}
            borderColor="gray.300"
            textAlign="right"
            verticalAlign="top"
          >
            <Input
              value={
                activeDiscountInput[index] === "amount"
                  ? row.tempDiscountAmount !== undefined
                    ? row.tempDiscountAmount
                    : ""
                  : row.discount && row.rspCyl
                    ? ((row.rspCyl * row.discount) / 100).toFixed(2)
                    : "0"
              }
              onChange={(e) => {
                const discountAmount = e.target.value;

                const updatedDiscounts = [...discounts];
                updatedDiscounts[index] = {
                  ...updatedDiscounts[index],
                  tempDiscountAmount: discountAmount,
                };
                setDiscounts(updatedDiscounts);

                if (discountAmount === "" || discountAmount === ".") return;

                const rspCyl = parseFloat(row.rspCyl) || 0;
                const amountValue = parseFloat(discountAmount);
                if (rspCyl === 0 || isNaN(amountValue)) return;

                const percentage = (amountValue / rspCyl) * 100;
                if (percentage > 100) return;

                updatedDiscounts[index] = {
                  ...updatedDiscounts[index],
                  discount: percentage.toFixed(2),

                  // ⭐ ADD THIS LINE
                  discountedAmount: amountValue,
                };

                setDiscounts(updatedDiscounts);
              }}
              onFocus={() => {
                setActiveDiscountInput({
                  ...activeDiscountInput,
                  [index]: "amount",
                });
                // Initialize temp value with calculated amount
                const calculatedAmount =
                  row.discount && row.rspCyl
                    ? ((row.rspCyl * row.discount) / 100).toFixed(2)
                    : "";
                const updatedDiscounts = [...discounts];
                updatedDiscounts[index] = {
                  ...updatedDiscounts[index],
                  tempDiscountAmount: calculatedAmount,
                };
                setDiscounts(updatedDiscounts);
              }}
              onBlur={() => {
                // Validate on blur
                const discountAmount = parseFloat(row.tempDiscountAmount);
                const rspCyl = parseFloat(row.rspCyl) || 0;

                if (!isNaN(discountAmount) && rspCyl > 0) {
                  const percentage = (discountAmount / rspCyl) * 100;

                  if (percentage > 100) {
                    toast({
                      title: "Invalid Discount Amount",
                      description: "Discount amount cannot exceed RSP value",
                      status: "error",
                      duration: 3000,
                      isClosable: true,
                    });
                    // Reset to valid value
                    const updatedDiscounts = [...discounts];
                    updatedDiscounts[index] = {
                      ...updatedDiscounts[index],
                      discount: 100,
                      tempDiscountAmount: undefined,
                    };
                    setDiscounts(updatedDiscounts);
                  }
                }

                setActiveDiscountInput({
                  ...activeDiscountInput,
                  [index]: null,
                });
                // Clear the temporary value
                const updatedDiscounts = [...discounts];
                updatedDiscounts[index].discountedAmount = discountAmount;
                delete updatedDiscounts[index].tempDiscountAmount;
                setDiscounts(updatedDiscounts);
              }}
              size="sm"
              type="number"
              isDisabled={!isEditing || shouldDisableEditing}
              bg={isEditing && !shouldDisableEditing ? "white" : "gray.50"}
              borderColor="gray.400"
              textAlign="right"
              width="100%"
              step="0.01"
              _focus={{
                borderColor: classicColors.accent,
                boxShadow: `0 0 0 1px ${classicColors.accent}`,
                bg: "white",
              }}
            />
          </Td>

          {/* Taxable Amount */}
          {/* Taxable Amount */}
          <Td
            p={3}
            borderColor="gray.300"
            textAlign="right"
            verticalAlign="top"
          >
            <Input
              value={
                row.discount && row.rspCyl
                  ? (row.rspCyl - (row.rspCyl * row.discount) / 100).toFixed(2)
                  : row.rspCyl || 0
              }
              size="sm"
              type="number"
              readOnly
              bg="gray.50"
              borderColor="gray.400"
              textAlign="right"
              width="100%"
            />
          </Td>

          {/* Effective Date */}
          <Td p={3} borderColor="gray.300" verticalAlign="top">
            <Box>
              <Input
                type="date"
                value={row.effectiveDate}
                onChange={(e) =>
                  handleChange(index, "effectiveDate", e.target.value)
                }
                size="sm"
                width="100%"
                isDisabled={!isEditing || shouldDisableEditing}
                borderColor={
                  discountErrors.effectiveDate ? "red.500" : "gray.400"
                }
                bg={discountErrors.effectiveDate ? "red.50" : "white"}
                _focus={{
                  borderColor: discountErrors.effectiveDate
                    ? "red.500"
                    : classicColors.accent,
                  boxShadow: `0 0 0 1px ${discountErrors.effectiveDate ? "red.500" : classicColors.accent}`,
                  bg: "white",
                }}
              />
              {discountErrors.effectiveDate && (
                <Text color="red.500" fontSize="xs" mt={1} whiteSpace="nowrap">
                  {discountErrors.effectiveDate}
                </Text>
              )}
            </Box>
          </Td>

          {/* Actions */}
          <Td
            p={3}
            borderColor="gray.300"
            textAlign="center"
            verticalAlign="top"
          >
            <Flex justify="center" gap={2}>
              {isEditing ? (
                <IconButton
                  size="sm"
                  bg={classicColors.success}
                  color="white"
                  icon={<Text fontSize="sm">✓</Text>}
                  onClick={() => handleSaveDiscount(index)}
                  aria-label="Save discount"
                  borderRadius="md"
                  _hover={{ bg: "#229954" }}
                  isDisabled={shouldDisableEditing || isViewOnly} // disable in view-only
                />
              ) : (
                <IconButton
                  size="sm"
                  bg={classicColors.accent}
                  color="white"
                  icon={<RiEditLine size={16} />}
                  onClick={() => handleEdit(index)}
                  aria-label="Edit discount"
                  isDisabled={shouldDisableEditing || isViewOnly} // disable in view-only
                  borderRadius="md"
                  _hover={{ bg: "#2980b9" }}
                />
              )}
              <IconButton
                size="sm"
                bg={classicColors.danger}
                color="white"
                icon={<RiDeleteBin7Line size={16} />}
                onClick={() => {
                  setDeleteIndex(index);
                  onOpen();
                }}
                aria-label="Delete discount"
                isDisabled={shouldDisableEditing || isViewOnly}
                borderRadius="md"
                _hover={{ bg: "#c0392b" }}
              />
            </Flex>
          </Td>
        </Tr>
      </>
    );
  };

  return (
    <>
      <Box
        bg="white"
        marginTop={5}
        p={{ base: 4, md: 6 }}
        border="1px solid"
        borderColor="gray.300"
        borderRadius="lg"
        boxShadow="lg"
      >
        {/* Header */}
        <Flex
          justify="space-between"
          align="center"
          mb={6}
          pb={4}
          borderBottom="2px solid"
          borderColor="gray.200"
        >
          <Text
            className="txt"
            fontWeight="bold"
            fontSize={{ base: "lg", md: "xl" }}
            color={classicColors.primary}
          >
            Credit Approval
          </Text>
          {shouldShowEditButton &&
            !isEditingCredit &&
            latestCreditEntry &&
            !isViewOnly && (
              <Button
                size="md"
                bg={classicColors.accent}
                color="white"
                leftIcon={<EditIcon size={16} />}
                onClick={handleEditCredit}
                borderRadius="md"
                _hover={{ bg: "#2980b9" }}
                boxShadow="md"
              >
                Edit
              </Button>
            )}
        </Flex>

        {/* Credit Form Fields */}
        <SimpleGrid
          columns={gridColumns}
          spacing={4}
          alignItems="center"
          mb={8}
        >
          {[
            {
              label: "Credit Limit",
              value: creditLimit,
              onChange: setCreditLimit,
              type: "input",
              required: true,
              disabled:
                isRejected ||
                (!isEditingCredit && !isUnderReview) ||
                isViewOnly,
              error: validationErrors.creditLimit,
            },
            {
              label: "Credit Limit Control",
              value: creditLimitControl,
              onChange: setCreditLimitControl,
              type: "select",
              options: [{ value: "2", label: "BLOCK" }],
              disabled:
                isRejected ||
                (!isEditingCredit && !isUnderReview) ||
                isViewOnly,
              error: validationErrors.creditLimitControl,
            },
            {
              label: "Credit Days",
              value: creditDays,
              onChange: setCreditDays,
              type: "select",
              options: [7, 14, 21, 28, 30, 45, 60, 90],
              required: true,
              disabled:
                isRejected ||
                (!isEditingCredit && !isUnderReview) ||
                isViewOnly,
              error: validationErrors.creditDays,
            },
            {
              label: "Credit Days Control",
              value: creditDaysControl,
              onChange: setCreditDaysControl,
              type: "select",
              options: [{ value: "2", label: "BLOCK" }],
              disabled:
                isRejected ||
                (!isEditingCredit && !isUnderReview) ||
                isViewOnly,
              error: validationErrors.creditDaysControl,
            },
          ].map((field, index) => (
            <Box key={index} minW={0}>
              <Text
                className="txt"
                fontSize="sm"
                mb={2}
                fontWeight="semibold"
                color={classicColors.dark}
              >
                {field.label}
              </Text>
              <Flex direction="column" minH="85px">
                {" "}
                {/* Adjust height if needed */}
                {field.type === "input" ? (
                  <Input
                    value={field.value}
                    onChange={(e) => field.onChange(e.target.value)}
                    size="md"
                    width="100%"
                    minW="120px"
                    placeholder={field.placeholder || ""}
                    isRequired={field.required}
                    isDisabled={field.disabled}
                    borderColor={field.error ? "red.500" : "gray.400"}
                    bg={field.error ? "red.50" : "white"}
                    _focus={{
                      borderColor: field.error
                        ? "red.500"
                        : classicColors.accent,
                      boxShadow: `0 0 0 1px ${field.error ? "red.500" : classicColors.accent}`,
                      bg: "white",
                    }}
                    borderRadius="md"
                  />
                ) : (
                  <Select
                    value={field.value}
                    onChange={(e) => field.onChange(e.target.value)}
                    size="md"
                    width="100%"
                    minW="120px"
                    placeholder={field.placeholder}
                    isRequired={field.required}
                    isDisabled={field.disabled}
                    borderColor={field.error ? "red.500" : "gray.400"}
                    bg={field.error ? "red.50" : "white"}
                    _focus={{
                      borderColor: field.error
                        ? "red.500"
                        : classicColors.accent,
                      boxShadow: `0 0 0 1px ${field.error ? "red.500" : classicColors.accent}`,
                      bg: "white",
                    }}
                    borderRadius="md"
                  >
                    {field.options.map((option) => (
                      <option
                        key={option.value || option}
                        value={option.value || option}
                      >
                        {option.label || option}
                      </option>
                    ))}
                  </Select>
                )}
                {/* Reserve space for error */}
                <Box minH="16px" mt={1}>
                  {field.error && (
                    <Text color="red.500" fontSize="xs">
                      {field.error}
                    </Text>
                  )}
                </Box>
              </Flex>
            </Box>
          ))}

          <Flex justify="flex-start" align="center" width="100%">
            <Button
              className="txt"
              bg={classicColors.success}
              color="white"
              _hover={{ bg: "#229954" }}
              size="md"
              onClick={handleAddDiscount}
              minW={{ base: "100%", sm: "140px" }}
              leftIcon={<Text fontSize="lg">+</Text>}
              isDisabled={isRejected || isViewOnly}
              borderRadius="md"
              boxShadow="md"
            >
              Add Product
            </Button>
          </Flex>
        </SimpleGrid>

        {/* Discount Table */}
        {discounts.length > 0 && (
          <Box mt={8}>
            {/* Desktop View */}
            <Box display={{ base: "none", lg: "block" }}>
              <TableContainer
                borderRadius="lg"
                boxShadow="lg"
                border="1px solid"
                borderColor="gray.300"
                overflowX="auto"
              >
                <Table
                  variant="simple"
                  size="md"
                  tableLayout="fixed"
                  width="100%"
                >
                  <Thead bg={classicColors.tableHeader}>
                    <Tr>
                      {[
                        { header: "Type", width: "12%" },
                        { header: "Product", width: "15%" },
                        { header: "RSP (Per Cyl.)", width: "10%" },
                        { header: "RSP (Per Kg)", width: "10%" },
                        { header: "Discount (%)", width: "10%" },
                        { header: "Discounted Amount", width: "12%" },
                        { header: "Taxable Amount", width: "12%" },
                        { header: "Effective Date", width: "14%" },
                        { header: "Actions", width: "5%" },
                      ].map(({ header, width }, index) => (
                        <Th
                          key={index}
                          color="white"
                          fontSize="sm"
                          fontWeight="bold"
                          textTransform="none"
                          borderColor="gray.400"
                          textAlign={
                            [2, 3, 5, 6].includes(index)
                              ? "right"
                              : index === 8
                                ? "center"
                                : "left"
                          }
                          py={3}
                          whiteSpace="nowrap"
                          width={width}
                        >
                          {header}
                        </Th>
                      ))}
                    </Tr>
                  </Thead>
                  <Tbody>
                    {discounts.map((row, index) =>
                      renderDiscountRow(row, index),
                    )}
                  </Tbody>
                </Table>
              </TableContainer>
            </Box>

            {/* Tablet View */}
            <Box display={{ base: "none", md: "block", lg: "none" }}>
              <TableContainer
                border="1px solid"
                borderColor="gray.300"
                borderRadius="lg"
                boxShadow="lg"
                overflowX="auto"
              >
                <Table variant="simple" size="sm" tableLayout="fixed">
                  <Thead bg={classicColors.tableHeader}>
                    <Tr>
                      {[
                        { header: "Type", width: "15%" },
                        { header: "Product", width: "20%" },
                        { header: "RSP (Cyl.)", width: "12%" },
                        { header: "RSP (Kg)", width: "12%" },
                        { header: "Discount", width: "12%" },
                        { header: "Effective Date", width: "19%" },
                        { header: "Actions", width: "10%" },
                      ].map(({ header, width }, index) => (
                        <Th
                          key={index}
                          color="white"
                          fontSize="sm"
                          fontWeight="bold"
                          textTransform="none"
                          borderColor="gray.400"
                          textAlign={index === 6 ? "center" : "left"}
                          py={3}
                          width={width}
                        >
                          {header}
                        </Th>
                      ))}
                    </Tr>
                  </Thead>
                  <Tbody>
                    {discounts.map((row, index) =>
                      renderDiscountRow(row, index),
                    )}
                  </Tbody>
                </Table>
              </TableContainer>
            </Box>

            {/* Mobile View */}
            <Box display={{ base: "block", md: "none" }}>
              <Box
                bg={classicColors.tableHeader}
                color="white"
                p={4}
                fontWeight="bold"
                fontSize="md"
                textAlign="center"
                borderRadius="lg"
                mb={4}
                boxShadow="md"
              >
                Discount Details
              </Box>
              <Box maxH="500px" overflowY="auto">
                {discounts.map((row, index) =>
                  renderDiscountRow(row, index, true),
                )}
              </Box>
            </Box>
          </Box>
        )}

        {/* Action Buttons */}
        <Flex
          justify={{ base: "center", md: "flex-end" }}
          mt={8}
          gap={4}
          flexWrap="wrap"
          direction={{ base: "column", sm: "row" }}
          pt={6}
          borderTop="1px solid"
          borderColor="gray.200"
        >
          {isEditingCredit ? (
            <>
              <Button
                bg={classicColors.success}
                color="white"
                size="md"
                onClick={handleSaveChangesWithValidation}
                minW={{ base: "100%", sm: "140px" }}
                isLoading={isSubmitting}
                loadingText="Saving"
                borderRadius="md"
                boxShadow="md"
                _hover={{ bg: "#229954" }}
                isDisabled={!hasChanges()} // Disable when no changes
              >
                Save Changes
              </Button>
              <Button
                bg="gray.500"
                color="white"
                size="md"
                onClick={handleCancelEdit}
                minW={{ base: "100%", sm: "140px" }}
                borderRadius="md"
                boxShadow="md"
                _hover={{ bg: "gray.600" }}
              >
                Cancel
              </Button>
            </>
          ) : (
            <>
              {!isViewOnly && (
                <>
                  {/* Show Approve/Reject buttons only when status is 0 (Under Review) */}
                  {isUnderReview && (
                    <>
                      <Button
                        bg={classicColors.success}
                        color="white"
                        size="md"
                        onClick={() => handleApproveWithValidation("3")}
                        minW={{ base: "100%", sm: "140px" }}
                        isLoading={isSubmitting}
                        loadingText="Approving"
                        borderRadius="md"
                        boxShadow="md"
                        _hover={{ bg: "#229954" }}
                      >
                        Approve
                      </Button>
                      <Button
                        bg={classicColors.danger}
                        color="white"
                        size="md"
                        onClick={handleReject}
                        minW={{ base: "100%", sm: "140px" }}
                        isLoading={isSubmitting}
                        loadingText="Rejecting"
                        borderRadius="md"
                        boxShadow="md"
                        _hover={{ bg: "#c0392b" }}
                      >
                        Reject
                      </Button>
                    </>
                  )}

                  {/* Show Save Changes button for approved applications or when not under review */}
                  {(isApproved || !isUnderReview) && (
                    <Button
                      bg={classicColors.success}
                      color="white"
                      className="txt"
                      size="md"
                      onClick={handleSaveChangesWithValidation}
                      minW={{ base: "100%", sm: "140px" }}
                      isLoading={isSubmitting}
                      loadingText="Saving"
                      variant="solid"
                      borderRadius="md"
                      boxShadow="md"
                      _hover={{ bg: "#229954" }}
                      isDisabled={!hasChanges()} // Disable when no changes
                    >
                      Save Changes
                    </Button>
                  )}
                </>
              )}
            </>
          )}
        </Flex>
      </Box>

      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Confirm Delete
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to delete this product?
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose}>
                No
              </Button>
              <Button colorScheme="red" onClick={confirmDelete} ml={3}>
                Yes
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      <AlertDialog
        isOpen={isSaveOpen}
        leastDestructiveRef={saveConfirmRef}
        onClose={onSaveClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Confirm Save Changes
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to save these changes?
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={saveConfirmRef} onClick={onSaveClose}>
                No
              </Button>
              <Button colorScheme="green" onClick={confirmSaveChanges} ml={3}>
                Yes
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default CreditApprovalSection;
